/*
 * MenuButton: a button with a drop-down of actions, in the kit's popout look (DataTable's column
 * menu: --surface-raised, radius 8, --shadow-popout; items 14/20, hover --interaction-hover).
 * Not in Figma: built from the tokens.
 *
 * Items keep the order given; `divider` draws a rule above an item (groups: everyday, then
 * occasional, then destructive). `danger` items are red (--text-danger): destructive actions,
 * which ask before they act. `hint` shows a keyboard shortcut on the right (--text-weaker).
 * Behaviour: opens on click, focus moves to the first item; ↑/↓ move, Enter picks, Esc closes
 * and returns focus to the button; a click outside closes it. With no items nothing renders.
 * Motion: the menu grows from its button's corner and folds back into it (styles/index.css,
 * .motion-pop); reopened while closing, it turns back from where it is.
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { Button } from './Button'
import { cx } from './cx'
import { caretDown16 } from './icons'
import { usePresence } from './usePresence'

export interface MenuItem {
    key: string
    label: string
    onPick: () => void
    danger?: boolean
    /** A rule above this item. */
    divider?: boolean
    /** Keyboard shortcut shown on the right, e.g. "Alt+X". */
    hint?: string
    disabled?: boolean
    /** A choice that is on or off (shown with a checkbox; role menuitemcheckbox). */
    checked?: boolean
    /** Leave the menu open after picking (several choices in a row, e.g. columns). */
    keepOpen?: boolean
}

export function MenuButton({ label, items, variant = 'secondary', align = 'left', icon, disabled, title }: {
    label: ReactNode
    items: MenuItem[]
    variant?: 'secondary' | 'ghost'
    align?: 'left' | 'right'
    icon?: string
    disabled?: boolean
    title?: string
}) {
    const [open, setOpen] = useState(false)
    const presence = usePresence(open)
    const ref = useRef<HTMLDivElement>(null)
    const menuId = useId()
    useEffect(() => {
        if (!open) return
        const close = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [open])
    useEffect(() => {
        if (open) ref.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled])')?.focus()
    }, [open])
    if (!items.length) return null

    function onKey(e: KeyboardEvent<HTMLDivElement>) {
        if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); ref.current?.querySelector<HTMLElement>('button')?.focus(); return }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
        e.preventDefault()
        const all = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])') ?? [])
        const i = all.indexOf(document.activeElement as HTMLElement)
        all[(i + (e.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length]?.focus()
    }

    return (
        <div ref={ref} className="relative flex" onKeyDown={onKey}>
            <Button variant={variant} trailingIcon={icon ?? caretDown16} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
                disabled={disabled} title={title} onClick={() => setOpen((o) => !o)}>
                {label}
            </Button>
            {presence.mounted && (
                <div id={menuId} role="menu" data-state={presence.state} inert={!open || undefined}
                    style={{ transformOrigin: align === 'right' ? 'top right' : 'top left' }}
                    className={cx('motion-pop absolute top-[calc(100%+8px)] z-30 flex max-h-[60vh] min-w-[220px] flex-col overflow-auto rounded-[8px] bg-[var(--surface-raised)] p-[4px] shadow-[var(--shadow-popout)]', align === 'right' ? 'right-0' : 'left-0')}>
                    {items.map((item) => (
                        <button key={item.key} type="button" role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'} aria-checked={item.checked}
                            disabled={item.disabled}
                            onClick={() => { if (!item.keepOpen) setOpen(false); item.onPick() }}
                            className={cx('flex cursor-pointer items-center justify-between gap-[24px] rounded-[6px] border-0 bg-transparent px-[8px] py-[6px] text-left text-[14px] leading-[20px] whitespace-nowrap hover:bg-[var(--interaction-hover)] active:bg-[var(--interaction-active)] focus-visible:bg-[var(--interaction-hover)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                                item.danger ? 'text-[var(--text-danger)]' : 'text-[var(--text-default)]',
                                item.divider && 'mt-[4px] border-t border-solid border-[var(--border-default)] pt-[8px]')}>
                            <span className="flex items-center gap-[10px]">
                                {item.checked !== undefined && <input type="checkbox" readOnly tabIndex={-1} aria-hidden checked={item.checked} className="pointer-events-none size-[16px] accent-[var(--accent-default)]" />}
                                {item.label}
                            </span>
                            {item.hint && <span className="text-[12px] text-[var(--text-weaker)] hover-none:hidden">{item.hint}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
